
import { defineComponent, PropType, ref } from "vue";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import { withListPage } from "@/core/helpers/list-page-context";
import {
  getErrorMsg,
  swalNotification,
} from "@/core/helpers/utils";
import { IVatTuResponseBase } from "@/core/interfaces/ApiResponses";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { VatTuService } from "@/core/services/VatTu.service";
import { usePagination } from "@/core/hooks/use-pagination";

import Setting from "@/components/shared/setting/Setting.vue";
import type { SettingAction } from "@/components/shared/setting/Setting.vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import VatTuDetailModal from "@/components/vat-tu/VatTuDetailModal.vue";
import useVatTu from "@/core/hooks/use-vat-tu";
import ConfirmationApproveDialog from "@/components/confirmation-approve-dialog/ConfirmationApproveDialog.vue";
import usePermission from "@/core/hooks/usePermission";
import IconCircleCheckClassicRegular from "@/icons/IconCircleCheckClassicRegular.vue";
import IconEye from "@/icons/IconEye.vue";
import IconPencil from "@/icons/IconPencil.vue";
import IconTrash from "@/icons/IconTrash.vue";

export default defineComponent({
  name: "danh-sach-vat-tu-table",

  components: {
    BaseLink,
    ConfirmationApproveDialog,
    ConfirmationDialog,
    Setting,
    VatTuDetailModal,
  },

  emits: ["delete-vat-tu-success", "on-approve-success", "refresh"],

  props: {
    curPage: {
      type: Number,
      default: 0,
    },
    items: {
      type: Array as PropType<Array<IVatTuResponseBase>>,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 0,
    },
    showPrice: {
      type: Boolean,
      default: false,
    },
    showSoDu: {
      type: Boolean,
      default: false,
    },
    showApproveButton: {
      type: Boolean,
      default: false,
    },
    showEditButton: {
      type: Boolean,
      default: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    vatTuPhieuNhapDetail: {
      type: Boolean,
      default: false,
    },
    /** When set (update page), highlight the row for the item being edited */
    selectedId: {
      type: Number,
      default: 0,
    },
  },

  setup() {
    const { push } = useRouterCustom();

    const { indexCal } = usePagination();

    const { isTextDangerColumn, isValid } = useVatTu();

    const selectedVatTuId = ref<number | undefined>(undefined);

    const { canDuyetVatTu } = usePermission();

    return {
      canDuyetVatTu,
      indexCal,
      isTextDangerColumn,
      isValid,
      push,
      selectedVatTuId,
    };
  },

  data() {
    return {
      approvingVatTu: null as IVatTuResponseBase | null,
      isDeleteVatTuDialogDisplay: false,
      deletingVatTu: null as IVatTuResponseBase | null,
      vatTuItems: this.items as IVatTuResponseBase[],
    };
  },

  watch: {
    items(value) {
      this.vatTuItems = value;
    },
  },

  computed: {
    isVatTuEditable() {
      return (
        process.env.VUE_APP_IS_EDIT_VAT_TU_ENABLE === "true" &&
        this.showEditButton
      );
    },
  },

  methods: {
    formatQuantity,
    formatUnitPrice,
    formatAmount,
    calcLineAmount,

    rowClassName({ row }: { row: IVatTuResponseBase }): string {
      if (this.selectedId > 0 && row.id === this.selectedId) {
        return "current-row";
      }

      return "";
    },

    getSettingActions(vatTu: IVatTuResponseBase): SettingAction[] {
      const actions: SettingAction[] = [
        {
          icon: IconEye,
          label: "Chi tiết",
          tooltip: "Thông tin chi tiết",
          onClick: () => {
            this.selectedVatTuId = vatTu.id;
          },
        },
      ];

      if (this.isVatTuEditable) {
        actions.push({
          icon: IconPencil,
          label: "Chỉnh sửa",
          tooltip: "Chỉnh sửa",
          any: ["master:vattu:sua"],
          onClick: () => {
            const route = this.$router.resolve(
              withListPage(
                {
                  name: "cap-nhat-vat-tu",
                  params: {
                    id: String(vatTu.id),
                  },
                },
                this.curPage
              )
            );

            window.open(route.href, "_blank");
          },
        });
      }

      if (
        this.showApproveButton &&
        this.canDuyetVatTu &&
        vatTu.is_valid === 0
      ) {
        actions.push({
          icon: IconCircleCheckClassicRegular,
          label: "Duyệt",
          tooltip: this.tooltipMessage(vatTu),
          any: ["master:vattu:duyet"],
          onClick: () => {
            this.approvingVatTu = vatTu;
          },
        });
      }

      if (this.showDeleteButton) {
        actions.push({
          icon: IconTrash,
          label: "Xoá",
          any: ["master:vattu:xoa"],
          onClick: () => {
            this.onDeleteVatTuClicked(vatTu.id);
          },
        });
      }

      return actions;
    },

    onDeleteVatTuClicked(vatTuId) {
      const vatTu = this.vatTuItems.find((item) => item.id === vatTuId);
      this.isDeleteVatTuDialogDisplay = true;
      if (vatTu) {
        this.deletingVatTu = vatTu;
      }
    },

    async confirmDelete() {
      this.isDeleteVatTuDialogDisplay = false;
      try {
        if (this.deletingVatTu) {
          await VatTuService.delete(this.deletingVatTu?.id);
        }
        await swalNotification("Xóa vật tư thành công", "success");
        this.$emit("delete-vat-tu-success");
      } catch (e) {
        await swalNotification(
          getErrorMsg(e, "Có lỗi xảy ra, không thể xóa vật tư này"),
          "error"
        );
      }
    },

    tooltipMessage(vatTu: IVatTuResponseBase) {
      if (!this.canDuyetVatTu) {
        return "Bạn không có quyền thực thiện tác vụ này";
      }

      if (vatTu.is_valid === 1) {
        return "Đã duyệt";
      }

      return "Duyệt";
    },
  },
});
